import React, {
  useRef,
  useMemo,
  useState,
  ReactNode,
  useCallback,
} from "react";
import { withRouter } from "react-router-dom";

import {
  ActionToolbar,
  ApartmentGuestsEditorButtonWithDialog,
  DialogWithActions,
  EditButton,
  GenerateApartmentsSurveysReport,
  GqlTable,
  ToggleApartmentShouldCompleteSurvey,
  UnlockApartmentReservationBlockButton,
} from "../../components";

import { Button, Container } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";

import { QUERIES } from "../../../graphql";
import { Apartment, CreateApartmentData } from "../../../graphql/models";
import { ApartmentForm } from "./apartment_form";

import useApartmentsStyle from "./apartments.style";
import { useUserState } from "../../../context";

function ApartmentsPage(props: any) {
  const classes = useApartmentsStyle();

  const tableRef = useRef();

  const { userRole } = useUserState();
  const [formDialogOpened, setFormDialogOpened] = useState(false);
  const [formApartment, setFormApartment] = useState<
    Apartment | CreateApartmentData | undefined
  >(void 0);

  const addNewApartment = () => {
    setFormApartment({} as Apartment);
    setFormDialogOpened(true);
  };

  const editApartment = (apartment) => {
    setFormApartment({
      ...apartment,
    });
    setFormDialogOpened(true);
  };

  const onValueUpdatedSuccessfully = () => {
    setFormApartment(void 0);
    setFormDialogOpened(false);
    console.log("refresh?");
    (tableRef as any).current?.refresh();
  };

  const gqlTableRowActions = useCallback(
    (row: Apartment) => {
      const actions: ReactNode[] = [];
      if (userRole === "system_admin") {
        actions.push(<EditButton onClick={() => editApartment(row)} />);
      }

      if (row.reservationConductStatus?.blocked === true) {
        actions.push(
          <UnlockApartmentReservationBlockButton
            apartment={row}
            onUnlocked={() => (tableRef as any).current?.refresh()}
          />
        );
      }

      if (userRole === "concierge" || userRole === "system_admin") {
        actions.push(
          <ApartmentGuestsEditorButtonWithDialog
            variant="contained"
            apartmentId={row.id}
            onAddOrRemove={() => (tableRef as any).current?.refresh()}
          >
            GESTISCI OSPITI
          </ApartmentGuestsEditorButtonWithDialog>
        );
      }

      return actions;
    },
    [userRole]
  );

  const actionToolbarActions = useMemo(() => {
    const actions: any[] = [];
    if (userRole === "system_admin") {
      actions.push(
        <GenerateApartmentsSurveysReport key="GenerateApartmentsSurveysReport" />
      );
      actions.push(
        <Button
          key="addNewApartment"
          variant="contained"
          color="primary"
          onClick={() => addNewApartment()}
        >
          Aggiungi Nuovo
        </Button>
      );
    }
    return actions;
  }, [userRole]);

  const columns = useMemo(() => {
    const cols = [
      "",
      "Palazzo",
      "Codice",
      "Proprietario",
      "Cell. Proprietario",
      "Email Proprietario",
      "Ospiti",
      "Questionario",
    ];
    if (userRole === "system_admin") {
      cols.push("Compilazione questionario richiesta?");
    }

    return cols;
  }, [userRole]);

  return (
    <Container className={classes.container} maxWidth={false} component="div">
      <>
        <ActionToolbar title={"Appartamenti"} actions={actionToolbarActions} />
        <GqlTable
          ref={tableRef}
          columns={columns.map((v, index) => (
            <TableCell key={index}>{v}</TableCell>
          ))}
          query={QUERIES.APARTMENTS}
          queryResultDataField="apartments"
          rows={(row: Apartment) => (
            <>
              <TableCell style={{ display: "flex", flexDirection: "row" }}>
                {gqlTableRowActions(row)}
              </TableCell>
              <TableCell>{row.building.name}</TableCell>
              <TableCell>{row.code}</TableCell>
              <TableCell>{row.owner?.name ?? "[Non Assegnato]"}</TableCell>
              <TableCell>{row.owner?.phone}</TableCell>
              <TableCell>{row.owner?.email}</TableCell>
              <TableCell>{row.users.map((u) => u.name).join(", ")}</TableCell>
              <TableCell>
                {row.shouldCompleteSurvey
                  ? "[DA COMPILARE]"
                  : row.lastSurveyCompletedDate
                  ? `Compilato il ${new Date(
                      row.lastSurveyCompletedDate
                    ).toLocaleString()}`
                  : "[COMPILAZIONE ANNULLATA]"}
              </TableCell>
              {userRole === "system_admin" && (
                <ToggleApartmentShouldCompleteSurvey
                  apartment={row}
                  onSetApartmentShouldCompleteSurveyChange={() =>
                    (tableRef as any).current?.refresh()
                  }
                />
              )}
            </>
          )}
          collapseContent={(row: Apartment) => (
            <div style={{ width: "100%", padding: "1rem" }}>
              {row.reservationOptions && (
                <ul>
                  {row.reservationOptions.map((v, index) => (
                    <li key={index}>
                      {v.service.name}: max {v.maxUnits} persone
                    </li>
                  ))}
                </ul>
              )}
              {(!row.reservationOptions ||
                row.reservationOptions.length === 0) &&
                "Non è stato configurato nessun limite sui servizi per questo appartamento"}
            </div>
          )}
        />
        <DialogWithActions
          fullScreen
          open={formDialogOpened}
          setOpen={setFormDialogOpened}
          title={"Aggiungi / Modifica Appartamento"}
        >
          <ApartmentForm
            value={formApartment}
            onValueUpdatedSuccessfully={onValueUpdatedSuccessfully}
          />
        </DialogWithActions>
      </>
    </Container>
  );
}

export default withRouter(ApartmentsPage);
